import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

export const HERO_SUN_COLOR = '#f97316'

export const heroSunDiscSizeClassName =
  'h-[min(300px,46vw)] w-[min(300px,46vw)] md:h-[min(340px,34vw)] md:w-[min(340px,34vw)]'

/** Daylight orange glow RGB — resting hero halo. */
export const HERO_SUN_GLOW_RGB = [249, 115, 22] as const

/** Sun glow at a given intensity — 1 is the resting glow, 0 removes it. */
export function heroSunGlowBoxShadow(
  intensity = 1,
  rgb: readonly [number, number, number] = HERO_SUN_GLOW_RGB,
): string {
  if (intensity <= 0) return 'none'
  const [r, g, b] = rgb
  return `0 0 60px rgba(${r},${g},${b},${0.35 * intensity}), 0 0 140px rgba(${r},${g},${b},${0.25 * intensity})`
}

export const heroSunDiscGlowStyle = {
  backgroundColor: HERO_SUN_COLOR,
  boxShadow: heroSunGlowBoxShadow(1),
} as const

export const heroSunDiscFlatStyle = {
  backgroundColor: HERO_SUN_COLOR,
} as const

export const heroSunDiscGrainClassName =
  'pointer-events-none absolute inset-0 rounded-[inherit] opacity-[0.18] mix-blend-soft-light [background-image:radial-gradient(circle_at_20%_25%,rgba(255,255,255,0.9)_0.6px,transparent_0.8px),radial-gradient(circle_at_80%_75%,rgba(0,0,0,0.8)_0.6px,transparent_0.8px)] [background-size:3px_3px,4px_4px]'

type HeroSunDiscProps = {
  className?: string
  /** When false, omits the outer orange glow. */
  glow?: boolean
}

/** Soft film-grain wash used on the hero sun disc. */
export function HeroSunDiscGrain({ className }: { className?: string }) {
  return <div aria-hidden className={cn(heroSunDiscGrainClassName, className)} />
}

/** Orange rising-sun disc — shared between hero and sun-tip sections. */
export const HeroSunDisc = forwardRef<HTMLDivElement, HeroSunDiscProps>(
  function HeroSunDisc({ className, glow = true }, ref) {
    return (
      <div
        ref={ref}
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-0 rounded-full',
          className,
        )}
        style={glow ? heroSunDiscGlowStyle : heroSunDiscFlatStyle}
      >
        <HeroSunDiscGrain className="rounded-full" />
      </div>
    )
  },
)
